import { DateTime } from "luxon";
import moment from "moment";

import { IPatientAppointmentModel as MyleInterfacesPatientIPatientAppointmentModel } from "@/Appointments/Models/UI/IPatientAppointmentModel";
import { Client } from "@/Common/Api/Client";
import { Email } from "@/Common/Api/Contacts/Dto/Email";
import { PhoneNumber } from "@/Common/Api/Contacts/Dto/PhoneNumber";
import { Employee } from "@/Common/Api/Employee/Dto/Employee";
import { IObstetricaLabResultsGroup } from "@/Common/Api/Labs/Dto/Responses/LabMessageGroupDTO";
import { TimerComponentModel } from "@/Common/Components/Inputs/TimerComponent";
import { ArrayHelper } from "@/Common/Helpers/ArrayHelper";
import { GuidHelper } from "@/Common/Helpers/GuidHelper";
import { PrescriptionHelper } from "@/Common/Helpers/PrescriptionHelper";
import { SearchMenuHelper } from "@/Common/Helpers/SearchMenuHelper";
import { EmployeeType } from "@/Common/Models/Employee/Enums/EmployeeType";
import { EnumMyleLoginPermission } from "@/Common/Models/EnumMyleLoginPermission";
import { Guid } from "@/Common/Models/Guid";
import { MedicalRequestType } from "@/Common/Models/MedicalRequest/Enum/MedicalRequestType";
import { Patient } from "@/Common/Models/Patient/Patient";
import { EditMode, MedicalSummaryMedicationDetailsSortColumn, PatientTab } from "@/Common/Models/Patient/PatientEnums";
import { PatientIdentifier } from "@/Common/Models/Patient/PatientIdentifier";
import { IPatientBannerModel } from "@/Common/Models/Patient/UI/IPatientBannerModel";
import { IPatientCliniciansModel } from "@/Common/Models/Patient/UI/Profile/IPatientCliniciansModel";
import { IPatientContactsModel } from "@/Common/Models/Patient/UI/Profile/IPatientContactsModel";
import { IPatientDemographicsModel } from "@/Common/Models/Patient/UI/Profile/IPatientDemographicsModel";
import { IPatientEnrollmentModel } from "@/Common/Models/Patient/UI/Profile/IPatientEnrollmentModel";
import { IPatientIdentifiersModel } from "@/Common/Models/Patient/UI/Profile/IPatientIdentifiersModel";
import { IPatientOtherInformationModel } from "@/Common/Models/Patient/UI/Profile/IPatientOtherInformationModel";
import { IPatientPortalProfileManagementModel } from "@/Common/Models/Patient/UI/Profile/IPatientPortalProfileManagementModel";
import { IPatientRelationshipsModel } from "@/Common/Models/Patient/UI/Profile/IPatientRelationshipsModel";
import { Person } from "@/Common/Models/Person/Person";
import { PortalRegistrationState } from "@/Common/Models/Portal/Enums/PortalRegistrationState";
import { GetPatientOverviewResponse } from "@/Common/Models/Portal/MedfarPortal";
import { ServiceModel } from "@/Common/Models/Services/ServiceModel";
import { RedirectTo, SortDirection } from "@/Common/Models/UI/Enums";
import { CheckboxTarget } from "@/Common/Models/UI/Inputs/CheckboxTarget";
import { ICheckable } from "@/Common/Models/UI/Inputs/ICheckable";
import { PopupModel } from "@/Common/Models/UI/PopupModel";
import { integrationBus } from "@/Common/Modularity/IntegrationBus/IntegrationBus";
import * as LocalOperations from "@/Common/Operations/operations.local";
import * as LocalPatientOperations from "@/Common/Operations/operations.patient.local";
import * as ServerOperations from "@/Common/Operations/operations.server";
import { SubOperationsEnum } from "@/Common/Operations/operations.server.sub";
import * as SubServerOperations from "@/Common/Operations/operations.server.sub";
import { CanadaState } from "@/Common/Resources/Lookups/Enums/CommonLookupCodes";
import { LocalEvent } from "@/Common/Stores/Actions/LocalEvent";
import { ServerEvent } from "@/Common/Stores/Actions/ServerEvent";
import { BaseStore } from "@/Common/Stores/BaseStore";
import { RequestMapper, ServerResponseMapper } from "@/Common/Stores/Handlers/Server/RequestMapper";
import ActionLockManager from "@/Common/Utilities/ActionLockManager";
import { isRouteMatchingLocation, RouterHistory } from "@/Common/Utilities/Router/RouterHistory";
import { IDocumentsTabModel } from "@/Documents/Models/UI/IDocumentsTabModel";
import { ILabsGlobalWrapper } from "@/Labs/Models/UI/ILabsGlobalWrapper";
import { ContactPointNotifiedInFutureAppointment } from "@/Patient/Api/Dto/Responses/ContactPointNotifiedInFutureAppointment";
import { EncounterDeletedHandler } from "@/Patient/Api/EncounterDeletedHandler";
import { BaseMedicalItemsViewState } from "@/Patient/Components/MedicalItems/BaseMedicalItemsViewState";
import { MedicationHelper } from "@/Patient/Helpers/MedicationHelper";
import { PatientMedicalItemsHelper } from "@/Patient/Helpers/PatientMedicalItemsHelper";
import { PatientModelHelper } from "@/Patient/Helpers/PatientModelHelper";
import { EncounterRelatedEmployee } from "@/Patient/Models/MedicalSummary/Encounter";
import { MedicalItemSubtypeEnum } from "@/Patient/Models/MedicalSummary/Enum/MedicalItemSubTypeEnum";
import { MedicalItemTypeEnum } from "@/Patient/Models/MedicalSummary/Enum/MedicalItemTypeEnum";
import { MedicalItem } from "@/Patient/Models/MedicalSummary/MedicalItem";
import { MedicalItemMedicationAndPrescriptionTableItem } from "@/Patient/Models/MedicalSummary/MedicalItemMedicationAndPrescriptionTableItem";
import { MedicalItemScreeningTestResponse } from "@/Patient/Models/MedicalSummary/Response/MedicalItemScreeningTestResponse";
import { GroupedMedicationModel, MedicationModel } from "@/Patient/Models/Medication/MedicationModel";
import { PrescriptionPrintHistory } from "@/Patient/Models/Medication/PrescriptionPrintHistory";
import { PopulationBasedFundingUpdateIconSignal } from "@/Patient/Models/PopulationBasedFunding/PopulatonBasedFundingStatusControllerUpdateSignal";
import { IPatientAppointmentModel as MyleInterfacesPatientPatientAppointmentIPatientAppointmentModel } from "@/Patient/Models/UI/Appointment/IPatientAppointmentModel";
import { IGrowthChartUiModel } from "@/Patient/Models/UI/Graph/IGrowthChartUiModel";
import { IPatientGraphUI } from "@/Patient/Models/UI/Graph/IPatientGraphUI";
import { IBillableService } from "@/Patient/Models/UI/History/IBillableService";
import { IPatientCommunicationsModel } from "@/Patient/Models/UI/History/IPatientCommunicationsModel";
import { IPatientMessagesModel } from "@/Patient/Models/UI/History/IPatientMessagesModel";
import { IPatientPrivateBillModel } from "@/Patient/Models/UI/History/IPatientPrivateBillModel";
import { IPatientRecallsModel } from "@/Patient/Models/UI/History/IPatientRecallsModel";
import { IPatientTasksModel } from "@/Patient/Models/UI/History/IPatientTasksModel";
import { IPatientUIModel } from "@/Patient/Models/UI/IPatientUIModel";
import { MedicationsStatus } from "@/Patient/Models/UI/MedicalNote/Enums/MedicalItem";
import { IPatientMedicalNoteModel } from "@/Patient/Models/UI/MedicalNote/IPatientMedicalNoteModel";
import { IMedicalItemsModel } from "@/Patient/Models/UI/MedicalSummary/IMedicalItemsModel";
import { IPatientMedicalSummaryModel } from "@/Patient/Models/UI/MedicalSummary/IPatientMedicalSummaryModel";
import { IMedicationTabModel } from "@/Patient/Models/UI/Medication/IMedicationTabModel";
import { IMedicationGridModel } from "@/Patient/Models/UI/Medication/IPatientMedicationGridModel";
import { IPrescriptionTabModel } from "@/Patient/Models/UI/Medication/IPrescriptionTabModel";
import { MedfarBillingAction } from "@/Patient/Stores/Actions/MedfarBillingAction";
import { PatientAppointmentAction } from "@/Patient/Stores/Actions/PatientAppointmentAction";
import { PatientFaxActions } from "@/Patient/Stores/Actions/PatientFaxActions";
import { PatientGrowthChartActions } from "@/Patient/Stores/Actions/PatientGrowthChartActions";
import { PatientHistoryActions } from "@/Patient/Stores/Actions/PatientHistoryActions";
import { PatientMedicalItemsActions } from "@/Patient/Stores/Actions/PatientMedicalItemsActions";
import { AppointmentStatusChangeEventType, PatientMedicalNoteActions } from "@/Patient/Stores/Actions/PatientMedicalNoteActions";
import { PatientMedicalSummaryActions } from "@/Patient/Stores/Actions/PatientMedicalSummaryActions";
import { PatientMedicationTabActions } from "@/Patient/Stores/Actions/PatientMedicationTabActions";
import { PatientPortalManagementActions } from "@/Patient/Stores/Actions/PatientPortalManagementAction";
import { PatientPrescriptionTabActions } from "@/Patient/Stores/Actions/PatientPrescriptionTabActions";
import { PatientProfileActions } from "@/Patient/Stores/Actions/PatientProfileActions";
import { PrivateBillActions } from "@/Patient/Stores/Actions/PrivateBillActions";
import { PatientGraphsHandler } from "@/Patient/Stores/Handlers/Local/Graphs/PatientGraphsHandler";
import { PatientGrowthChartsHandler } from "@/Patient/Stores/Handlers/Local/GrowthCharts/PatientGrowthChartsHandler";
import { PatientHistoryAppointmentHandler } from "@/Patient/Stores/Handlers/Local/History/PatientHistoryAppointmentHandler";
import { PatientHistoryCommunicationsHandler } from "@/Patient/Stores/Handlers/Local/History/PatientHistoryCommunicationsHandler";
import { PatientHistoryHandler } from "@/Patient/Stores/Handlers/Local/History/PatientHistoryHandler";
import { PatientHistoryMessageHandler } from "@/Patient/Stores/Handlers/Local/History/PatientHistoryMessageHandler";
import { PatientHistoryPrivateBillHandler } from "@/Patient/Stores/Handlers/Local/History/PatientHistoryPrivateBillHandler";
import { PatientHistoryRecallsHandler } from "@/Patient/Stores/Handlers/Local/History/PatientHistoryRecallsHandler";
import { PatientHistoryTaskHandler } from "@/Patient/Stores/Handlers/Local/History/PatientHistoryTaskHandler";
import { MedfarBillingHandler } from "@/Patient/Stores/Handlers/Local/MedfarBilling/MedfarBillingHandler";
import { PatientMedicalItemsHandler } from "@/Patient/Stores/Handlers/Local/MedicalItems/MedicalItemsHandler";
import { MedicalNotePopupHandler } from "@/Patient/Stores/Handlers/Local/MedicalNote/MedicalNotePopupHandler";
import { PatientMedicalNoteHandler } from "@/Patient/Stores/Handlers/Local/MedicalNote/PatientMedicalNoteHandler";
import { PatientMedicalSummaryHandler } from "@/Patient/Stores/Handlers/Local/MedicalSummary/PatientMedicalSummaryHandler";
import { PatientMedicationGridHandler } from "@/Patient/Stores/Handlers/Local/Medication/PatientMedicationGridHandler";
import { PatientMedicationTabHandler } from "@/Patient/Stores/Handlers/Local/Medication/PatientMedicationTabHandler";
import { PatientPrescriptionTabHandler } from "@/Patient/Stores/Handlers/Local/Medication/PatientPrescriptionTabHandler";
import { PatientAppointmentHandler } from "@/Patient/Stores/Handlers/Local/PatientAppointmentHandler";
import { PatientTabHandler } from "@/Patient/Stores/Handlers/Local/PatientTabHandler";
import { PatientPopulationBasedFundingHandler } from "@/Patient/Stores/Handlers/Local/Profile/PatientPopulationBasedFundingHandler";
import { PatientProfileCliniciansHandler } from "@/Patient/Stores/Handlers/Local/Profile/PatientProfileCliniciansHandler";
import { PatientProfileContactsHandler } from "@/Patient/Stores/Handlers/Local/Profile/PatientProfileContactsHandler";
import { PatientProfileDemographicsHandler } from "@/Patient/Stores/Handlers/Local/Profile/PatientProfileDemographicsHandler";
import { PatientProfileEnrollmentHandler } from "@/Patient/Stores/Handlers/Local/Profile/PatientProfileEnrollmentHandler";
import { PatientProfileHandler } from "@/Patient/Stores/Handlers/Local/Profile/PatientProfileHandler";
import { PatientProfileIdentifiersHandler } from "@/Patient/Stores/Handlers/Local/Profile/PatientProfileIdentifiersHandler";
import { PatientProfileOtherInfoHandler } from "@/Patient/Stores/Handlers/Local/Profile/PatientProfileOtherInfoHandler";
import { PatientProfileManagementHandler } from "@/Patient/Stores/Handlers/Local/Profile/PatientProfilePortalManagementHandler";
import { PatientProfileRelationshipsHandler } from "@/Patient/Stores/Handlers/Local/Profile/PatientProfileRelationshipsHandler";
import { MedfarBillingResponseHandler } from "@/Patient/Stores/Handlers/Server/MedfarBilling/MedfarBillingResponseHandler";
import { PatientAppointmentResponseHandler } from "@/Patient/Stores/Handlers/Server/PatientAppointmentResponseHandler";
import { PatientGrowthChartResponseHandler } from "@/Patient/Stores/Handlers/Server/PatientGrowthChartResponseHandler";
import { PatientHistoryResponseHandler } from "@/Patient/Stores/Handlers/Server/PatientHistoryResponseHandler";
import { PatientMedicalItemsResponseHandler } from "@/Patient/Stores/Handlers/Server/PatientMedicalItemsResponseHandler";
import { PatientMedicalNoteResponseHandler } from "@/Patient/Stores/Handlers/Server/PatientMedicalNoteResponseHandler";
import { PatientMedicalSummaryResponseHandler } from "@/Patient/Stores/Handlers/Server/PatientMedicalSummaryResponseHandler";
import { PatientMedicationTabResponseHandler } from "@/Patient/Stores/Handlers/Server/PatientMedicationTabResponseHandler";
import { PatientPrescriptionTabResponseHandler } from "@/Patient/Stores/Handlers/Server/PatientPrescriptionTabResponseHandler";
import { PatientProfilePortalManagementResponseHandler } from "@/Patient/Stores/Handlers/Server/PatientProfilePortalManagementResponseHandler";
import { PatientProfileResponseHandler } from "@/Patient/Stores/Handlers/Server/PatientProfileResponseHandler";
import { PatientRAMQEnrolmentResponseHandler } from "@/Patient/Stores/Handlers/Server/PatientRAMQEnrolmentResponseHandler";
import { CleanEncountersCacheEvent } from "../IntegrationBus/PatientEvents";
import { IFlowsheetModel } from "../Models/JSONInterfaces/Flowsheet/IFlowsheetModel";
import { FlowsheetActions } from "./Actions/FlowsheetAction";
import { FlowsheetsResponseHandler } from "./Handlers/Local/Flowsheets/FlowsheetsResponseHandler";
import { PatientFlowsheetsHandler } from "./Handlers/Local/Flowsheets/PatientFlowsheetsHandler";
import { PatientHistoryPortalCommunicationsHandler } from "./Handlers/Local/History/PatientHistoryPortalCommunicationsHandler";

let defaultInstance: PatientStore;

const actionLock = ActionLockManager.getInstance();

interface IPatientContributorsEncounterData {
    summaryModel: IPatientMedicalSummaryModel;
    noteModel: IPatientMedicalNoteModel;
}

class PatientStore extends BaseStore {
    private ignorePublishingRules: boolean; // this appointment scheduler filter should persist between patient files
    private ActivePatientContactId: Guid;
    private PatientCacheModels: { [patientContactId: string]: IPatientUIModel } = {};
    private LocationHistory: { [patientContactId: string]: string } = {}; // patient redirect history

    // mappers
    private Mapper: RequestMapper;
    private ServerMapper: ServerResponseMapper;

    // handlers server events
    private ServerHandlerProfile: PatientProfileResponseHandler;
    private ServerHandlerHistory: PatientHistoryResponseHandler;
    private ServerHandlerMedicalSummary: PatientMedicalSummaryResponseHandler;
    private ServerHandlerMedicalNote: PatientMedicalNoteResponseHandler;
    private ServerHandlerMedfarBilling: MedfarBillingResponseHandler;
    private ServerHandlerPrescriptionTab: PatientPrescriptionTabResponseHandler;
    private ServerHandlerMedicationTab: PatientMedicationTabResponseHandler;
    private ServerRAMQEnrolmentHandler: PatientRAMQEnrolmentResponseHandler;
    private ServerHandlerAppointment: PatientAppointmentResponseHandler;
    private ServerHandlerMedicalItems: PatientMedicalItemsResponseHandler;
    private ServerHandlerPortal: PatientProfilePortalManagementResponseHandler;
    private ServerhandlerGrowthChart: PatientGrowthChartResponseHandler;
    private readonly ServerHandlerFlowsheets: FlowsheetsResponseHandler;

    // patient file allowlist
    private PatientIdsDeniedToCurrentUser: Guid[] = [];

    public CanEditPatient: boolean;
    public CanCreatePatient: boolean;
    public CanEditPatientEmail: boolean;
    public CanEditPatientRelationship: boolean;

    // shared property to notify whether a tab editor is visible
    // because it is activated by main component state and can only target1 clickable text component at a time
    public ClickableTextEditorActive: boolean;

    // common models
    public IsLoading = false;

    public PatientUIModels: { [patientContactId: string]: IPatientUIModel } = {};
    public PatientModels: { [patientContactId: string]: Patient } = {};
    public PatientBannerModels: { [patientContactId: string]: IPatientBannerModel } = {};
    public PatientProfileDemographicModels: { [patientContactId: string]: IPatientDemographicsModel } = {};

    // patient profile models
    public PatientProfileDemographicUndoModels: { [patientContactId: string]: IPatientDemographicsModel } = {};
    public PatientProfileContactsModels: { [patientContactId: string]: IPatientContactsModel } = {};
    public PatientProfileContactsUndoModels: { [patientContactId: string]: IPatientContactsModel } = {};
    public PatientProfileIdentifiersModels: { [patientContactId: string]: IPatientIdentifiersModel } = {};
    public PatientProfileIdentifiersUndoModels: { [patientContactId: string]: IPatientIdentifiersModel } = {};
    public PatientProfileCliniciansModels: { [patientContactId: string]: IPatientCliniciansModel } = {};
    public PatientProfileCliniciansUndoModels: { [patientContactId: string]: IPatientCliniciansModel } = {};
    public PatientProfileOtherIdentificationModels: { [patientContactId: string]: IPatientOtherInformationModel } = {};
    public PatientProfileOtherIdentificationUndoModels: { [patientContactId: string]: IPatientOtherInformationModel } = {};
    public PatientProfileEnrollmentModels: { [patientContactId: string]: IPatientEnrollmentModel } = {};
    public PatientProfileEnrollmentUndoModels: { [patientContactId: string]: IPatientEnrollmentModel } = {};
    public PatientProfileRelationshipsModels: { [patientContactId: string]: IPatientRelationshipsModel } = {};
    public PatientProfileRelationshipsUndoModels: { [patientContactId: string]: IPatientRelationshipsModel } = {};
    public PatientPortalOverviewModels: { [patientContactId: string]: IPatientPortalProfileManagementModel } = {};

    // patient history models
    public PatientHistoryMessagesModels: { [patientContactId: string]: IPatientMessagesModel } = {};
    public PatientHistoryTasksModels: { [patientContactId: string]: IPatientTasksModel } = {};
    public PatientHistoryCommunicationsModels: { [patientContactId: string]: IPatientCommunicationsModel } = {};
    public PatientHistoryRecallsModels: { [patientContactId: string]: IPatientRecallsModel } = {};
    public PatientHistoryAppointmentsModels: { [patientContactId: string]: MyleInterfacesPatientIPatientAppointmentModel } = {};
    public PatientHistoryBillsModels: { [patientContactId: string]: IPatientPrivateBillModel } = {};
    public PatientHistoryBillableServicesModels: { [patientContactId: string]: IBillableService[] } = {};
    public patientHistoryPortalCommunicationsCurrentThread: { [patientContactId: string]: string } = {};

    // patient appointment models
    public PatientAppointmentModels: { [patientContactId: string]: MyleInterfacesPatientPatientAppointmentIPatientAppointmentModel } = {};

    // patient medical summary models
    public PatientMedicalSummaryModels: { [patientContactId: string]: IPatientMedicalSummaryModel } = {};

    // patient medical note models
    public PatientMedicalNoteModels: { [patientContactId: string]: IPatientMedicalNoteModel } = {};
    public PatientMedicalNoteUndoModels: { [patientContactId: string]: IPatientMedicalNoteModel } = {};
    public PatientMedicalNotesCache: { [patientContactId: string]: IPatientMedicalNoteModel[] } = {};

    // patient medical items models
    public PatientMedicalItemsModels: { [patientContactId: string]: IMedicalItemsModel } = {};

    // models for documents and results integration
    public PatientDocuments: { [patientContactId: string]: IDocumentsTabModel } = {};
    public PatientLabs: { [patientContactId: string]: ILabsGlobalWrapper } = {};
    public PatientLabObstetricObservations: { [patientContactId: string]: IObstetricaLabResultsGroup[] } = {};

    // Medication
    public PatientPrescriptionTabModels: { [patientContactId: string]: IPrescriptionTabModel } = {};
    public PatientMedicationTabModels: { [patientContactId: string]: IMedicationTabModel } = {};
    public PatientMedicationModels: { [patientContactId: string]: IMedicationTabModel } = {};
    public PatientMedicationGridModels: { [patientContactId: string]: IMedicationGridModel } = {};
    public PatientMedicationGridUndoModels: { [patientContactId: string]: IMedicationGridModel } = {};

    // Vital signs
    public PatientChartsUIStateModels: { [patientContactId: string]: IPatientGraphUI } = {};

    // Growth Charts
    public GrowthChartUiModels: { [patientContactId: string]: IGrowthChartUiModel } = {};

    // MedicalItem state cache
    public MedicalItemsStateCache: { [patientContactId: string]: { [medicalItemId: string]: BaseMedicalItemsViewState } } = {};

    // Flowsheets
    public FlowsheetModels: { [patientContactId: Guid]: IFlowsheetModel[] } = {};

    // Pbf Icon Update Signal
    public PopulationBasedFundingUpdateIconSignal: PopulationBasedFundingUpdateIconSignal = new PopulationBasedFundingUpdateIconSignal({});

    // Billing History
    public PatientUpdateBillingHistorySignal: number = 1;

    // patient profile context
    public IsMedicalItemFromOutsideNote: boolean;
    public IsCreatingNewMedicalItem: boolean;
    public MedicalItemPreviousRoute: LocalPatientOperations.MedicalNote;
    public SelectedMedicalProblemSubType: MedicalItemSubtypeEnum;

    // patient profile client event handlers
    public PatientProfileHandler: PatientProfileHandler;
    public PatientProfileDemographicsHandler: PatientProfileDemographicsHandler;
    public PatientProfileContactsHandler: PatientProfileContactsHandler;
    public PatientProfileOtherInfoHandler: PatientProfileOtherInfoHandler;
    public PatientProfileIdentifiersHandler: PatientProfileIdentifiersHandler;
    public PatientProfileCliniciansHandler: PatientProfileCliniciansHandler;
    public PatientProfileRelationshipsHandler: PatientProfileRelationshipsHandler;
    public PatientProfileEnrollmentHandler: PatientProfileEnrollmentHandler;
    public PatientProfilePortalManagementHandler: PatientProfileManagementHandler;

    // patient history client event handlers
    public PatientHistoryHandler: PatientHistoryHandler;
    public PatientHistoryAppointmentHandler: PatientHistoryAppointmentHandler;
    public PatientHistoryMessageHandler: PatientHistoryMessageHandler;
    public PatientHistoryTaskHandler: PatientHistoryTaskHandler;
    public PatientHistoryRecallsHandler: PatientHistoryRecallsHandler;
    public PatientHistoryCommunicationsHandler: PatientHistoryCommunicationsHandler;
    public PatientHistoryPrivateBillHandler: PatientHistoryPrivateBillHandler;
    public PatientHistoryPortalCommunicationsHandler: PatientHistoryPortalCommunicationsHandler;

    // other handlers
    public PatientHandler: PatientTabHandler;
    public AppointmentHandler: PatientAppointmentHandler;

    // medical summary / CPP handlers
    public PatientMedicalSummaryHandler: PatientMedicalSummaryHandler;
    public PatientMedicalItemsHandler: PatientMedicalItemsHandler;

    // Medication
    public PatientMedicationGridHandler: PatientMedicationGridHandler;
    public PatientPrescriptionTabHandler: PatientPrescriptionTabHandler;
    public PatientMedicationTabHandler: PatientMedicationTabHandler;

    // medical note
    public PatientMedicalNoteHandler: PatientMedicalNoteHandler;
    public PatientMedicalNotePopupHandler: MedicalNotePopupHandler;

    // Medfar Billing
    public MedfarBillingHandler: MedfarBillingHandler;

    // Graphs
    public PatientGraphsHandler: PatientGraphsHandler;

    // Growth Charts
    public PatientGrowthChartsHandler: PatientGrowthChartsHandler;

    // population based funding
    public PatientPopulationBasedFundingHandler: PatientPopulationBasedFundingHandler;

    // Flowsheets
    public PatientFlowsheetsHandler: PatientFlowsheetsHandler;

    // actions
    public ActionsProfile: PatientProfileActions;
    public ActionsHistory: PatientHistoryActions;
    public ActionsMedicalSummary: PatientMedicalSummaryActions;
    public ActionsMedicalNote: PatientMedicalNoteActions;
    public ActionsPrescriptionTab: PatientPrescriptionTabActions;
    public ActionsMedicationTab: PatientMedicationTabActions;
    public ActionsAppointment: PatientAppointmentAction;
    public ActionsFax: PatientFaxActions;
    public ActionsMedfarBilling: MedfarBillingAction;
    public ActionsMedicalItems: PatientMedicalItemsActions;
    public ActionsPortalManagement: PatientPortalManagementActions;
    public ActionsGrowthCharts: PatientGrowthChartActions;
    public ActionsPrivateBill: PrivateBillActions;
    public FlowsheetActions: FlowsheetActions;

    public vigilanceAlertsDebounceTimeoutReference: number;

    public vigilanceAnalysisDebounceTimeoutReference: number;

    public searchId: number;
    public lastReceivedSearchId: number;

    public get shouldIgnorePublishingRules(): boolean {
        return this.ignorePublishingRules;
    }

    constructor() {
        super();

        globalServerResponseHandler.addErrorHandler(new EncounterDeletedHandler(this));

        this.ClickableTextEditorActive = false;

        // new constructor implementation to receive 2 optional (but necessary) functioins: onBeforeExecutePatientCalls | onAfterExecutePatientCalls
        // onBeforeExecutePatientCalls will fire before ajax call is made and if returns false, ajax call will be interrupted
        // onAfterExecutePatientCalls will fire when ajax call is complete (success, user still logged in) just PRIOR dispatching response over ServerDispatcher
        // return false in onAfterExecutePatientCalls will interrupt dispatch. We do this if patient file is closed for patient id in the response. We simply never dispatch the call to handlers.

        this.ActionsProfile = new PatientProfileActions(this.onBeforeExecutePatientCalls.bind(this), this.onAfterExecutePatientCalls.bind(this));
        this.ActionsHistory = new PatientHistoryActions(this.onBeforeExecutePatientCalls.bind(this), this.onAfterExecutePatientCalls.bind(this));
        this.ActionsMedicalSummary = new PatientMedicalSummaryActions(this.onBeforeExecutePatientCalls.bind(this), this.onAfterExecutePatientCalls.bind(this));
        this.ActionsAppointment = new PatientAppointmentAction(this.onBeforeExecutePatientCalls.bind(this), this.onAfterExecutePatientCalls.bind(this));
        this.ActionsMedicalNote = new PatientMedicalNoteActions(this.onBeforeExecutePatientCalls.bind(this), this.onAfterExecutePatientCalls.bind(this));
        this.ActionsFax = new PatientFaxActions(this.onBeforeExecutePatientCalls.bind(this), this.onAfterExecutePatientCalls.bind(this));
        this.ActionsPrescriptionTab = new PatientPrescriptionTabActions(this.onBeforeExecutePatientCalls.bind(this), this.onAfterExecutePatientCalls.bind(this));
        this.ActionsMedicationTab = new PatientMedicationTabActions(this.onBeforeExecutePatientCalls.bind(this), this.onAfterExecutePatientCalls.bind(this));
        this.ActionsMedfarBilling = new MedfarBillingAction(this.onBeforeExecutePatientCalls.bind(this), this.onAfterExecutePatientCalls.bind(this));
        this.ActionsMedicalItems = new PatientMedicalItemsActions(this.onBeforeExecutePatientCalls.bind(this), this.onAfterExecutePatientCalls.bind(this));
        this.ActionsPortalManagement = new PatientPortalManagementActions(this.onBeforeExecutePatientCalls.bind(this), this.onAfterExecutePatientCalls.bind(this));
        this.ActionsGrowthCharts = new PatientGrowthChartActions(this.onBeforeExecutePatientCalls.bind(this), this.onAfterExecutePatientCalls.bind(this));
        this.ActionsPrivateBill = new PrivateBillActions();
        this.FlowsheetActions = new FlowsheetActions(this.onBeforeExecutePatientCalls.bind(this), this.onAfterExecutePatientCalls.bind(this));

        this.Mapper = new RequestMapper();
        this.ServerMapper = new ServerResponseMapper();

        this.CanEditPatientRelationship = true;
        this.IsMedicalItemFromOutsideNote = false;

        if (sessionStore.clientConfiguration && sessionStore.clientConfiguration.PatientEditRelationshipDisabled)
            this.CanEditPatientRelationship = false;

        this.CanEditPatient = cacheStore.ClinicSettings?.EnablePatientEdit || sessionStore.hasPermission(EnumMyleLoginPermission.CanCreateAndEditPatientPermission);
        this.CanCreatePatient = cacheStore.ClinicSettings?.EnablePatientEdit || sessionStore.hasPermission(EnumMyleLoginPermission.CanCreateAndEditPatientPermission);
        this.CanEditPatientEmail = cacheStore.ClinicSettings?.EnablePatientEditEmail;

        this.PatientProfileHandler = new PatientProfileHandler();
        this.PatientProfileDemographicsHandler = new PatientProfileDemographicsHandler();
        this.PatientProfileContactsHandler = new PatientProfileContactsHandler();
        this.PatientProfileOtherInfoHandler = new PatientProfileOtherInfoHandler();
        this.PatientProfileIdentifiersHandler = new PatientProfileIdentifiersHandler();
        this.PatientProfileCliniciansHandler = new PatientProfileCliniciansHandler();
        this.PatientProfileRelationshipsHandler = new PatientProfileRelationshipsHandler();
        this.PatientProfileEnrollmentHandler = new PatientProfileEnrollmentHandler();
        this.PatientProfilePortalManagementHandler = new PatientProfileManagementHandler();

        this.PatientHistoryHandler = new PatientHistoryHandler();
        this.PatientHistoryAppointmentHandler = new PatientHistoryAppointmentHandler();
        this.PatientHistoryMessageHandler = new PatientHistoryMessageHandler();
        this.PatientHistoryTaskHandler = new PatientHistoryTaskHandler();
        this.PatientHistoryRecallsHandler = new PatientHistoryRecallsHandler();
        this.PatientHistoryCommunicationsHandler = new PatientHistoryCommunicationsHandler();
        this.PatientHistoryPrivateBillHandler = new PatientHistoryPrivateBillHandler();
        this.PatientHistoryPortalCommunicationsHandler = new PatientHistoryPortalCommunicationsHandler();

        this.PatientHandler = new PatientTabHandler();
        this.AppointmentHandler = new PatientAppointmentHandler();

        this.PatientMedicalSummaryHandler = new PatientMedicalSummaryHandler();
        this.PatientMedicalItemsHandler = new PatientMedicalItemsHandler();

        this.PatientMedicationGridHandler = new PatientMedicationGridHandler();
        this.PatientPrescriptionTabHandler = new PatientPrescriptionTabHandler();
        this.PatientMedicationTabHandler = new PatientMedicationTabHandler();

        this.PatientMedicalNoteHandler = new PatientMedicalNoteHandler();
        this.PatientMedicalNotePopupHandler = new MedicalNotePopupHandler();

        this.MedfarBillingHandler = new MedfarBillingHandler();

        this.PatientGraphsHandler = new PatientGraphsHandler();
        this.PatientGrowthChartsHandler = new PatientGrowthChartsHandler();
        this.PatientFlowsheetsHandler = new PatientFlowsheetsHandler();

        this.PatientPopulationBasedFundingHandler = new PatientPopulationBasedFundingHandler();

        this.ServerHandlerProfile = new PatientProfileResponseHandler(this.ActionsProfile);
        this.ServerHandlerHistory = new PatientHistoryResponseHandler(this.ActionsHistory);
        this.ServerHandlerMedicalSummary = new PatientMedicalSummaryResponseHandler(this.ActionsMedicalSummary);
        this.ServerHandlerMedicalNote = new PatientMedicalNoteResponseHandler(this.ActionsMedicalNote);
        this.ServerHandlerMedfarBilling = new MedfarBillingResponseHandler(this.ActionsMedfarBilling);
        this.ServerHandlerPrescriptionTab = new PatientPrescriptionTabResponseHandler(this.ActionsPrescriptionTab);
        this.ServerHandlerMedicationTab = new PatientMedicationTabResponseHandler(this.ActionsMedicationTab);
        this.ServerRAMQEnrolmentHandler = new PatientRAMQEnrolmentResponseHandler(this.ActionsProfile);
        this.ServerHandlerAppointment = new PatientAppointmentResponseHandler(this.ActionsAppointment);
        this.ServerHandlerMedicalItems = new PatientMedicalItemsResponseHandler(this.ActionsMedicalItems);
        this.ServerHandlerPortal = new PatientProfilePortalManagementResponseHandler(this.ActionsPortalManagement);
        this.ServerhandlerGrowthChart = new PatientGrowthChartResponseHandler(this.ActionsGrowthCharts);
        this.ServerHandlerFlowsheets = new FlowsheetsResponseHandler(this.FlowsheetActions);

        this.Mapper.AddInstance(this.PatientProfileHandler);
        this.Mapper.AddInstance(this.PatientProfileDemographicsHandler);
        this.Mapper.AddInstance(this.PatientProfileContactsHandler);
        this.Mapper.AddInstance(this.PatientProfileOtherInfoHandler);
        this.Mapper.AddInstance(this.PatientProfileIdentifiersHandler);
        this.Mapper.AddInstance(this.PatientProfileCliniciansHandler);
        this.Mapper.AddInstance(this.PatientProfileRelationshipsHandler);
        this.Mapper.AddInstance(this.PatientProfileEnrollmentHandler);
        this.Mapper.AddInstance(this.PatientProfilePortalManagementHandler);

        this.Mapper.AddInstance(this.PatientHistoryHandler);
        this.Mapper.AddInstance(this.PatientHistoryAppointmentHandler);
        this.Mapper.AddInstance(this.PatientHistoryMessageHandler);
        this.Mapper.AddInstance(this.PatientHistoryTaskHandler);
        this.Mapper.AddInstance(this.PatientHistoryRecallsHandler);
        this.Mapper.AddInstance(this.PatientHistoryCommunicationsHandler);
        this.Mapper.AddInstance(this.PatientHistoryPrivateBillHandler);
        this.Mapper.AddInstance(this.PatientHistoryPortalCommunicationsHandler);
        this.Mapper.AddInstance(this.PatientMedicalSummaryHandler);
        this.Mapper.AddInstance(this.PatientMedicalItemsHandler);
        this.Mapper.AddInstance(this.PatientMedicationGridHandler);
        this.Mapper.AddInstance(this.PatientPrescriptionTabHandler);
        this.Mapper.AddInstance(this.PatientMedicationTabHandler);
        this.Mapper.AddInstance(this.PatientMedicalNoteHandler);
        this.Mapper.AddInstance(this.PatientMedicalNotePopupHandler);
        this.Mapper.AddInstance(this.MedfarBillingHandler);
        this.Mapper.AddInstance(this.PatientHandler);
        this.Mapper.AddInstance(this.AppointmentHandler);
        this.Mapper.AddInstance(this.PatientGraphsHandler);
        this.Mapper.AddInstance(this.PatientGrowthChartsHandler);
        this.Mapper.AddInstance(this.PatientPopulationBasedFundingHandler);
        this.Mapper.AddInstance(this.PatientFlowsheetsHandler);

        this.ServerMapper.AddInstance(this.ServerHandlerProfile);
        this.ServerMapper.AddInstance(this.ServerHandlerHistory);
        this.ServerMapper.AddInstance(this.ServerHandlerMedicalSummary);
        this.ServerMapper.AddInstance(this.ServerHandlerMedicalNote);
        this.ServerMapper.AddInstance(this.ServerRAMQEnrolmentHandler);
        this.ServerMapper.AddInstance(this.ServerHandlerAppointment);
        this.ServerMapper.AddInstance(this.ServerHandlerMedfarBilling);
        this.ServerMapper.AddInstance(this.ServerHandlerPrescriptionTab);
        this.ServerMapper.AddInstance(this.ServerHandlerMedicationTab);
        this.ServerMapper.AddInstance(this.ServerHandlerMedicalItems);
        this.ServerMapper.AddInstance(this.ServerHandlerPortal);
        this.ServerMapper.AddInstance(this.ServerhandlerGrowthChart);
        this.ServerMapper.AddInstance(this.ServerHandlerFlowsheets);

        this.ClientDispatchToken = LocalEventDispatcher.register((request: LocalEvent) => {
            this.Mapper.Map(request, this, this.ActivePatientContactId);
        });

        this.ServerDispatchToken = ServerResponseDispatcher.register((response: ServerEvent) => {
            this.ServerMapper.Map(response, this);
        });

        MainComponentStateDispatcher.register((response: LocalEvent) => {
            switch (response.Operation) {
                case LocalOperations.MainComponentViewOperations.DisplayCreateClickableText:
                    this.ClickableTextEditorActive = true;
                    break;

                case LocalOperations.MainComponentViewOperations.EditorClosed:
                    this.ClickableTextEditorActive = false;
                    break;
            }
        });

        this.searchId = 1;
        this.lastReceivedSearchId = 0;
        this.ignorePublishingRules = false;
    }

    // feature toggle for patient note
    // user will have access if HasPatientNoteAccess=1 or HasPatientNoteAces=null
    public get PatientNoteDisabled(): boolean {
        let hasPatientNoteAccess = sessionStore.getCurrentEmployee().HasPatientNoteAccess;
        return (hasPatientNoteAccess != null && hasPatientNoteAccess != undefined)
            ? !hasPatientNoteAccess
            : false;
    }

    public static createSingleton(): PatientStore {
        return this.createStoreSingleton(defaultInstance, () => defaultInstance = new PatientStore());
    }

    public static createSingletonFrom(store: PatientStore, copy: boolean = true): PatientStore {
        return copy
            ? Object.assign(this.createSingleton(), store)
            : this.createStoreSingleton(defaultInstance, () => defaultInstance = store);
    }

    public isDSQActive(): boolean {
        if (sessionStore.isMsaActive) {
            let currentPatient = this.getCurrentPatient();
            if (currentPatient != null)
                return currentPatient.isPatientDSQSynced();
        }
        return false;
    }

    public resolvePatientContactId(contactFacetId: string): string {
        return Object.keys(this.PatientModels)
            .map(p => this.PatientModels[p])
            .firstOrDefault(p => p.PatientResponse.ContactFacet_id == contactFacetId)?.IndividualResponse.Contact_id;
    }

    public getCurrentPatient(): Patient {
        return patientStore.PatientModels[this.ActivePatientContactId];
    }

    public getCurrentPatientContactId(): Guid {
        return this.ActivePatientContactId;
    }

    public setCurrentPatientContactId(patientId: Guid): void {
        this.ActivePatientContactId = patientId;
    }

    public addPatientIdDeniedToCurrentUser(patientId: Guid): void {
        if (this.PatientIdsDeniedToCurrentUser.contains(patientId))
            return;

        delete this.LocationHistory[patientId];
        this.PatientIdsDeniedToCurrentUser.push(patientId);
    }

    public removePatientIdDeniedToCurrentUser(patientId: Guid): void {
        if (this.PatientIdsDeniedToCurrentUser.contains(patientId)) {
            this.PatientIdsDeniedToCurrentUser = this.PatientIdsDeniedToCurrentUser.filter(id => id != patientId);
        }
    }

    public isPatientFileDeniedToCurrentUser(patientId: Guid): boolean {
        return this.PatientIdsDeniedToCurrentUser.contains(patientId);
    }

    public get hasCurrentPatient(): boolean {
        return !!this.getCurrentPatient();
    }

    public savePatientClosedRedirectLocation(patientId: string, location: string): void {
        this.LocationHistory[patientId] = location;
    }

    public setActivePatientUI(patientContactId: Guid, changingPatients: boolean = false, changeSubTabCallback: () => void = null, redirectToPatientTab: boolean = true, forceReload: boolean = false): void {
        // log with ID
        console.debug(`[ActivePatientFile] Setting active patient: ${patientContactId}.`);

        if (!this.tryLockPatientFile(patientContactId)) {
            console.debug("[ActivePatientFile] Patient file is already locked.");
            return;
        }

        if (changingPatients) {
            this.swapDocumentModels(patientContactId);
            this.swapLabModels(patientContactId);
        }

        if (!Object.keys(this.PatientUIModels).some(id => id === patientContactId) || forceReload) {
            console.debug("[ActivePatientFile] Loading patient.", patientContactId);
            this.IsLoading = true;
            this.ActionsProfile.loadProtectedPatient(
                patientContactId,
                () => {
                    changeSubTabCallback?.();

                    console.debug("[ActivePatientFile] Loading patient completed.", patientContactId);

                    if (redirectToPatientTab)
                        this.redirectToPatientFile(patientContactId);

                    this.loadActivePatientCumulativeProfile(patientContactId, () => {
                        this.resolveSetActivePatientUI(patientContactId);
                    });
                },
                SubServerOperations.Patient.SetActivePatient
            );
        }
        else {
            console.debug("[ActivePatientFile] Not reloading patient.", patientContactId);
            this.ActivePatientContactId = patientContactId;

            if (redirectToPatientTab)
                this.redirectToPatientFile(patientContactId);

            this.loadActivePatientCumulativeProfile(patientContactId, () => {
                this.handlePatientAlreadyLoaded(patientContactId, changingPatients, changeSubTabCallback);
            });
        }
    }

    private redirectToPatientFile(patientContactId: Guid): void {
        console.debug("[ActivePatientFile] Redirection to patient tab.", patientContactId);
        sessionStore.dispatch(LocalOperations.Person.OpenFile, { person: patientStore.getPersonModel(patientContactId) }, 0);
    }

    private resolveSetActivePatientUI(patientContactId: Guid): void {
        this.UnlockPatientFile(patientContactId);
        overlayStore.hideOverlay("global-overlay");
        patientStore.EmitChangeToListeners();
    }

    private handlePatientAlreadyLoaded(
        patientContactId: Guid,
        changingPatients: boolean,
        changeSubTabCallback: () => void
    ): void {
        let currentTabView =
            this.PatientUIModels[patientContactId] == null
                ? PatientTab.Profile
                : this.PatientUIModels[patientContactId].get("ActiveVerticalTab");

        changeSubTabCallback && changeSubTabCallback();

        if (changingPatients && currentTabView === PatientTab.Documents) {
            window.setTimeout(() => documentsStore.EmitChangeToListeners(), 0);
        }

        if (changingPatients && currentTabView === PatientTab.Results) {
            window.setTimeout(() => labsStore.EmitChangeToListeners(), 0);
        }

        const encounterEditId = this.PatientUIModels[patientContactId].get("EditEncounterIdContext");
        if (encounterEditId) {
            sessionStore.setEncounterTimer(new TimerComponentModel(patientContactId, encounterEditId));
        }

        this.resolveSetActivePatientUI(patientContactId);
    }

    private loadActivePatientCumulativeProfile(activePatientContactId: string, then?: () => void): void {
        if (!sessionStore.hasRightsForAction([EmployeeType.Practician])) {
            then?.();
            return;
        }

        try {
            console.debug("[ActivePatientFile] Loading active patient cumulative profile.", activePatientContactId);

            patientStore.ActionsMedicalSummary.LoadPatientCumulativeProfile(
                activePatientContactId,
                null,
                true,
                true,
                "PatientTab_ActivePatientFileChanged",
                () => {
                    console.debug("[ActivePatientFile] Patient cumulative profile loaded.", activePatientContactId);
                    let patient = patientStore.PatientModels[activePatientContactId];
                    if (patient != null) {
                        let currentModel = patientStore.PatientBannerModels[activePatientContactId];

                        // here we need to delete and reset immutable's underlying  object property so the reference from immutable to that obect is broken
                        // we then reset it, with a new patient model. in that way, patientbanner's should component update will see two different models and trigger a render
                        currentModel = currentModel.delete("Model");
                        currentModel = currentModel.set("Model", patient);
                        patientStore.PatientBannerModels[activePatientContactId] = currentModel;
                    }
                    then?.();
                }
            );
        }
        catch (error) {
            console.error("Error loading active patient cumulative profile:", error);
        }
    }

    public isPatientTabDisplayed(): boolean {
        return isRouteMatchingLocation("patients");
    }

    public showPatientTab(): void {
        if (!this.isPatientTabDisplayed()) {
            RouterHistory.push("patients");
            window.setTimeout(() => patientStore.EmitChangeToListeners(), 0);
        }
    }

    public closeCurrentPatientFile(redirect: boolean): void {
        sessionStore.setEncounterTimer(null, true, false);

        delete this.PatientCacheModels[this.ActivePatientContactId];

        delete this.PatientUIModels[this.ActivePatientContactId];
        delete this.PatientModels[this.ActivePatientContactId];
        delete this.PatientBannerModels[this.ActivePatientContactId];
        delete this.PatientProfileDemographicModels[this.ActivePatientContactId];

        // patient redirect history
        let redirectLocation = this.LocationHistory[this.ActivePatientContactId];
        delete this.LocationHistory[this.ActivePatientContactId];

        // patient profile models
        delete this.PatientProfileDemographicUndoModels[this.ActivePatientContactId];
        delete this.PatientProfileContactsModels[this.ActivePatientContactId];
        delete this.PatientProfileContactsUndoModels[this.ActivePatientContactId];
        delete this.PatientProfileIdentifiersModels[this.ActivePatientContactId];
        delete this.PatientProfileIdentifiersUndoModels[this.ActivePatientContactId];
        delete this.PatientProfileCliniciansModels[this.ActivePatientContactId];
        delete this.PatientProfileCliniciansUndoModels[this.ActivePatientContactId];
        delete this.PatientProfileOtherIdentificationModels[this.ActivePatientContactId];
        delete this.PatientProfileOtherIdentificationUndoModels[this.ActivePatientContactId];
        delete this.PatientProfileEnrollmentModels[this.ActivePatientContactId];
        delete this.PatientProfileEnrollmentUndoModels[this.ActivePatientContactId];
        delete this.PatientProfileRelationshipsModels[this.ActivePatientContactId];
        delete this.PatientProfileRelationshipsUndoModels[this.ActivePatientContactId];
        delete this.PatientPortalOverviewModels[this.ActivePatientContactId];

        // patient history models
        delete this.PatientHistoryMessagesModels[this.ActivePatientContactId];
        delete this.PatientHistoryTasksModels[this.ActivePatientContactId];
        delete this.PatientHistoryCommunicationsModels[this.ActivePatientContactId];
        delete this.PatientHistoryRecallsModels[this.ActivePatientContactId];
        delete this.PatientHistoryAppointmentsModels[this.ActivePatientContactId];
        delete this.PatientHistoryBillsModels[this.ActivePatientContactId];
        delete this.PatientHistoryBillableServicesModels[this.ActivePatientContactId];
        delete this.patientHistoryPortalCommunicationsCurrentThread[this.ActivePatientContactId];

        // patient appointment models
        delete this.PatientAppointmentModels[this.ActivePatientContactId];

        // patient medical summary models
        delete this.PatientMedicalSummaryModels[this.ActivePatientContactId];

        // clear all medical item state from this patient
        delete this.MedicalItemsStateCache[this.ActivePatientContactId];

        delete this.PatientMedicalSummaryModels[this.ActivePatientContactId];

        // patient medical note models
        delete this.PatientMedicalNoteModels[this.ActivePatientContactId];
        delete this.PatientMedicalNoteUndoModels[this.ActivePatientContactId];

        // patient medical items models
        delete this.PatientMedicalItemsModels[this.ActivePatientContactId];

        // models for documents and results integration
        delete this.PatientDocuments[this.ActivePatientContactId];
        delete this.PatientLabs[this.ActivePatientContactId];
        delete this.PatientLabObstetricObservations[this.ActivePatientContactId];

        // Medication
        delete this.PatientPrescriptionTabModels[this.ActivePatientContactId];
        delete this.PatientMedicationTabModels[this.ActivePatientContactId];
        delete this.PatientMedicationModels[this.ActivePatientContactId];
        delete this.PatientMedicationGridModels[this.ActivePatientContactId];

        delete this.PatientChartsUIStateModels[this.ActivePatientContactId];

        delete this.GrowthChartUiModels[this.ActivePatientContactId];

        delete this.FlowsheetModels[this.ActivePatientContactId];

        // clear loaded store
        if (labsStore.Model?.get("PatientContactId") == this.ActivePatientContactId)
            labsStore.setDefaultModels();

        // manage appointment state for current patient
        this.ActionsMedicalNote.ManagePatientAppointmentState(this.ActivePatientContactId, null, moment().toDate(), AppointmentStatusChangeEventType.OnPatientClose);

        this.setCurrentPatientContactId(null);

        if (redirect) {
            window.setTimeout(() => RouterHistory.push(redirectLocation || "calendar"), 0);
            this.EmitChangeToListeners();
        }
    }

    public getDefaultPatientUIModel(patient: Patient): IPatientUIModel {
        let uiModelJson = PatientModelHelper.getDefaultPatientUIModel();
        uiModelJson.PatientContactFacetId = patient.PatientResponse.ContactFacet_id;
        uiModelJson.PatientContactId = patient.IndividualResponse.Contact_id;
        uiModelJson.PatientFirstName = patient.IndividualResponse.FirstName;
        uiModelJson.PatientLastName = patient.IndividualResponse.LastName;
        uiModelJson.PatientPreferredName = patient.IndividualResponse.PreferredName;
        uiModelJson.PatientPreferredPronouns = patient.IndividualResponse.PreferredPronouns;
        uiModelJson.PatientSpecifiedPreferredPronouns = patient.IndividualResponse.SpecifiedPreferredPronouns;
        uiModelJson.GenderIdentity = patient.IndividualResponse.GenderIdentity;
        uiModelJson.ShowProfileMemoPopup = (patient.IndividualResponse.MemoNote && patient.IndividualResponse.MemoNote.length > 0);
        uiModelJson.AvailableDeactivationReasonLookupIds = cacheStore.FindLookupsByTypeCode("INACTIVITYREASON");

        let uiModel: IPatientUIModel = Immutable.fromJS(uiModelJson);

        return uiModel;
    }

    public isIdentifiersViewValid(): boolean {
        let isValid = true;
        let idModel = this.PatientProfileIdentifiersModels[this.ActivePatientContactId];
        if (idModel) {
            idModel.get("HealthCards").toArray().forEach(x => {
                if (!x.IsValid || (x.Number == null || IsNullOrWhiteSpace(x.Number.trim())))
                    isValid = false;
            });
        }
        return isValid;
    }

    public isClinicianViewValid(): boolean {
        let isValid = true;
        let idModel = this.PatientProfileCliniciansModels[this.ActivePatientContactId];

        if (idModel) {
            idModel.get("IntervenorsOnFile").toArray().forEach(x => {
                if (!x.IsValid())
                    isValid = false;
            });
        }
        return isValid;
    }

    public saveDocumentStoreState(): void {
        const docModel = documentsStore.DocumentsModel;

        if (!Object.keys(this.PatientDocuments).contains(docModel.get("PatientContactId")))
            return; // patient probably closed

        this.PatientDocuments[docModel.get("PatientContactId")] = docModel;
    }

    public saveLabsStoreState(): void {
        const labsInboxModel = labsStore.LabsInboxModel;

        if (!Object.keys(this.PatientLabs).contains(labsInboxModel.get("PatientContactId")))
            return; // patient probably closed

        this.PatientLabs[labsInboxModel.get("PatientContactId")] = labsStore.ExportLabModels();
    }

    public getHealthCardIdForActivePatient(): string {
        const lookupId = cacheStore.GetLookupIdForProvince(CanadaState.Quebec);
        const healthCards = this.PatientModels[this.ActivePatientContactId].HealthCards.filter(card => card.ProvinceLookupId === lookupId);

        return healthCards.length > 0
            ? healthCards[0].Number
            : null;
    }

    public isContactsViewValid(): boolean {
        const contactModel = this.PatientProfileContactsModels[this.ActivePatientContactId];

        if (contactModel) {
            return contactModel.get("Addresses").toArray().all(address => address.IsValid || IsNullOrWhiteSpace(address.ZipCode))
                && contactModel.get("PhoneNumbers").toArray().all(phone => phone.IsValid || PhoneNumber.IsEmpty(phone))
                && contactModel.get("Emails").toArray().all(email => email.IsValid || Email.IsEmpty(email));
        }
        return true;
    }

    public hasAnyPatients(): boolean {
        return Object.keys(this.PatientProfileDemographicUndoModels).length > 0;
    }

    public getPersonModel(id: Guid | string): Person {
        if (Object.keys(this.PatientModels).some(x => x == id)) {
            let patient = this.PatientModels[id];
            return Person.FromPatient(patient);
        }
        else return null;
    }

    public getDefaultServicesBySiteAndEmployee(selectedSite: string, selectedEmployee: Employee): string[] {
        return this
            .getDefaultServiceModelssBySiteAndEmployee(selectedSite, selectedEmployee)
            .map(x => x.GetNameWithDuration());
    }

    public getDefaultServiceModelssBySiteAndEmployee(selectedSite: string, selectedEmployee: Employee): ServiceModel[] {
        return selectedEmployee && selectedSite
            ? selectedEmployee.ServicesOffered
                .filter(x => x.ServiceDefinitionModel.Model.SiteId === selectedSite && !x.ServiceDefinitionModel.IsAdHoc)
            : [];
    }

    public getMedicalItemCachedState(patientId: string, itemid: string): BaseMedicalItemsViewState {
        if (!patientId || !itemid)
            return null;

        let result = null;
        if (Object.keys(this.MedicalItemsStateCache).indexOf(patientId) > -1) {
            let parentItems = this.MedicalItemsStateCache[patientId];
            if (parentItems != null)
                result = parentItems[itemid];
        }
        return result;
    }

    public getMedicalItemCachedStateGeneric<T extends BaseMedicalItemsViewState>(patientContactId: string, cacheId: string): T {
        return this.getMedicalItemCachedState(patientContactId, cacheId) as T;
    }

    public setMedicalItemCachedState(patientId: string, itemid: string, item: BaseMedicalItemsViewState): void {
        if (patientId && itemid && this.PatientModels[patientId] /* patient still loaded */) {
            let patientItems = this.MedicalItemsStateCache[patientId] || {};
            patientItems[itemid] = item;

            this.MedicalItemsStateCache[patientId] = patientItems;
        }
    }

    public deleteMedicalItemCachedState(patientId: string, itemid: string): void {
        if (patientId) {
            if (Object.keys(this.MedicalItemsStateCache).indexOf(patientId) > -1) {
                let patientItems = this.MedicalItemsStateCache[patientId];
                if (patientItems != null)
                    delete patientItems[itemid];
            }
        }
    }

    public getCurrentlyEditedMedicalItemId(): string {
        if (this.PatientMedicalItemsModels[this.ActivePatientContactId] != null) {
            if (this.PatientMedicalItemsModels[this.ActivePatientContactId].get("MedicalItemsList").count() > 0) {
                let result = this.PatientMedicalItemsModels[this.ActivePatientContactId].get("MedicalItemsList").find(obj => obj.IsCommitted === false);
                if (result != null)
                    return result.MedicalItemId;
            }
        }
        return "";
    }

    private swapDocumentModels(nextPatientContactId: string): void {
        const patientContactId = documentsStore.getCurrentModelPatientId();
        if (patientContactId != nextPatientContactId) {
            documentsStore.SaveState();
            documentsStore.ImportDocumentsModel(this.PatientDocuments[nextPatientContactId]);
        }
    }

    private swapLabModels(nextPatientContactId: string): void {
        const patientContactId = labsStore.getCurrentModelPatientId();
        if (patientContactId !== nextPatientContactId) {
            labsStore.SaveState();
            labsStore.ImportLabsModels(this.PatientLabs[nextPatientContactId]);
            labsStore.LoadSettings(true);
        }
    }

    /**
     *
     * @param serverOperation is the Server operation
     * @param subOperation is the sub operation
     * @param messageId unique message id
     * @param data data (JSON payload) to be sent to server. It can be altered here (if you really have good reasons for it) or can be logged prior each request
     * @param dto special multi-purpose object to which you can assign any PROPERTIES with data. Ex: dto.name = Dan; This object will be available in onAfterExecutePatientCalls
     */
    private onBeforeExecutePatientCalls(serverOperation: number, subOperation: SubOperationsEnum, messageId: string, data: any, dto: any): boolean {
        dto.id = patientStore.ActivePatientContactId;
        return true; // do nothing for now
    }

    /**
     *
     * @param response of type ServerEvent
     * @param messageId unique messageId matching request messageId
     * @param dto special multi-purpose object to READ properties from. See @param dto in onBeforeExecutePatientCalls
     */
    private onAfterExecutePatientCalls(response: ServerEvent, messageId: string, dto: any): boolean {
        if (this.PatientModels[dto.id] == null
                && response.Operation !== ServerOperations.Server.Myle_LoadPatient
                && response.Operation !== ServerOperations.Server.Myle_LoadProtectedPatient) {
            patientStore.IsLoading = false;
            return false;
        }
        response.PatientId = dto.id;
        return true; // do nothing for now
    }

    public openPatient(patientContactId: string): void {
        let openPatientOperation = new LocalEvent(LocalOperations.Misc.RedirectToTab);

        openPatientOperation.AddItem("value", patientContactId);
        openPatientOperation.AddItem("target", RedirectTo.Patient);
        openPatientOperation.AddItem("currentLocation", RouterHistory.location);
        LocalEventDispatcher.dispatch(openPatientOperation);
    }

    public convertMedicationsToTableItems(patientId: string, items: MedicationModel[], detailsChecks: Immutable.List<ICheckable>): MedicalItemMedicationAndPrescriptionTableItem[] {
        let medicationGridModel = this.PatientMedicationGridModels[patientId];
        let searchString = medicationGridModel.get("SearchText");
        let sortColumn = medicationGridModel.get("SortColumn");
        let sortDirection = medicationGridModel.get("SortDirection");

        let showInstructions = false;
        let instructionCheckboxes = detailsChecks.toArray().filter(x => x.Target == CheckboxTarget.Myle_PatientCPP_MedicationSearch_Details_InstructionsCheck);
        if (instructionCheckboxes.length > 0)
            showInstructions = instructionCheckboxes[0].Checked;

        items = items.filter(x => {
            let isValid = true;
            if (searchString && searchString.length > 0) {
                const medicationName = SearchMenuHelper.Helper.RemoveDiacritics(x.GetName(true).toLowerCase());
                const search = SearchMenuHelper.Helper.RemoveDiacritics(searchString.toLowerCase());

                const isPrescribedExternally = PrescriptionHelper.IsPrescribedExternally(x.Model.ExternalPrescriberDisplayName);
                const prescriber = cacheStore.ClinicSettings.EnablePrescriberDatesAndComplianceFields && isPrescribedExternally
                    ? SearchMenuHelper.Helper.RemoveDiacritics(x.Model.ExternalPrescriberDisplayName.toLowerCase())
                    : SearchMenuHelper.Helper.RemoveDiacritics(Employee.getFullNameWithPrefixContactFacet(x.Model.EmployeeId).toLowerCase()) ?? "";

                isValid = medicationName.indexOf(search) >= 0 || prescriber.indexOf(search) >= 0;
            }
            return isValid;
        });

        if (sortColumn == null && sortColumn == undefined)
            sortColumn = MedicalSummaryMedicationDetailsSortColumn.Medication;
        if (sortDirection == null || sortDirection == undefined)
            sortDirection = SortDirection.ASC;

        switch (sortColumn) {
            case MedicalSummaryMedicationDetailsSortColumn.PatientCompliance:
                items = items
                    .mutate(item => item["IsPatientCompliant"] = item.Model.IsPatientCompliant)
                    .sortByProperty(item => item["IsPatientCompliant"], sortDirection == SortDirection.DESC);
                break;

            case MedicalSummaryMedicationDetailsSortColumn.Prescriber:
                items = items
                    .sortByProperty(
                        item => cacheStore.ClinicSettings.EnablePrescriberDatesAndComplianceFields && PrescriptionHelper.IsPrescribedExternally(item.Model.ExternalPrescriberDisplayName)
                            ? item.Model.ExternalPrescriberDisplayName ?? ""
                            : Employee.getFullNameWithPrefixContactFacet(item.Model.EmployeeId) ?? "",
                        sortDirection == SortDirection.DESC
                    );
                break;

            case MedicalSummaryMedicationDetailsSortColumn.WrittenDate:
                items = items
                    .mutate(item => item["WrittenDate"] = item.Model.WrittenDate
                        ? PatientMedicalItemsHelper.GetLifeStageDateString(
                            item.Model.WrittenDate,
                            item.Model.WrittenDatePrecision
                        )
                        : ""
                    )
                    .sortByProperty(item => item["WrittenDate"], sortDirection == SortDirection.DESC);
                break;

            case MedicalSummaryMedicationDetailsSortColumn.Beginning:
                items = items
                    .mutate(item => {
                        if (cacheStore.ClinicSettings.EnablePrescriberDatesAndComplianceFields) {
                            item["RefillStartDate"] = item.Model.RefillStartDate
                                ? PatientMedicalItemsHelper.GetLifeStageDateString(
                                    item.Model.RefillStartDate,
                                    item.Model.StartDatePrecision
                                )
                                : "";
                        }
                        else {
                            item["RefillStartDate"] = item.Model.RefillStartDate ? moment(item.Model.RefillStartDate).format("YYYY-MM-DD") : "";
                        }
                    })
                    .sortByProperty(item => item["RefillStartDate"], sortDirection == SortDirection.DESC);
                break;

            case MedicalSummaryMedicationDetailsSortColumn.Dosage:
                items = items.map(x => {
                    x["dosageSortString"] = x.GetDosageString();
                    return x;
                });
                items = ArrayHelper.SortDynamic(items, "dosageSortString", sortDirection == SortDirection.DESC);
                break;

            case MedicalSummaryMedicationDetailsSortColumn.Ended:
                items = items.map(x => {
                    x["RefillEndDate"] = x.EffectiveEndDate() != null ? moment(x.EffectiveEndDate()).format("YYYY-MM-DD") : "";
                    return x;
                });
                items = ArrayHelper.SortDynamic(items, "RefillEndDate", sortDirection == SortDirection.DESC);
                break;

            case MedicalSummaryMedicationDetailsSortColumn.Medication:
                let groups: { [key: string]: GroupedMedicationModel } = {};
                items.forEach(o => {
                    let key1 = o.Model.Name;
                    let key2 = o.Model.CodeGen;
                    const alternativeKey = o.Model.Id;
                    if (groups[key1] != null) {
                        groups[key1].Items.push(o);
                    }
                    else if (groups[key2] != null) {
                        groups[key2].Items.push(o);
                    }
                    else {
                        let newGroup = new GroupedMedicationModel();
                        newGroup.key = !IsNullOrWhiteSpace(key2) ? key2 : !IsNullOrWhiteSpace(key1) ? key1 : alternativeKey;
                        newGroup.Items = [];
                        groups[newGroup.key] = newGroup;
                        groups[newGroup.key].Items.push(o);
                    }
                });
                let groupsArray = Object.keys(groups)
                    .sort((a, b) => a.localeCompare(b))
                    .map(group => groups[group]);

                let sortedItems: MedicationModel[] = [];
                groupsArray.forEach(x => {
                    x.Items = x.Items.sortByPropertyDesc(y => y.Model.RefillStartDate ? moment(y.Model.RefillStartDate) : "");
                });
                groupsArray.sortByProperty(x => x.Items[0].Model.Name).forEach(x => {
                    x.Items.forEach(y => sortedItems.push(y));
                });

                items = sortedItems;
                break;

            case MedicalSummaryMedicationDetailsSortColumn.Origin:
                items = items.map(x => {
                    x["originSortString"] = x.GetOriginString();
                    return x;
                });
                items = ArrayHelper.SortDynamic(items, "originSortString", sortDirection == SortDirection.DESC);
                break;

            case MedicalSummaryMedicationDetailsSortColumn.Quantity:
                items = items.mutate(x => x["RefillEndDate"] = x.Model.RefillEndDate);
                items = ArrayHelper.SortDynamic(items, "RefillEndDate", sortDirection == SortDirection.DESC);
                break;

            case MedicalSummaryMedicationDetailsSortColumn.Renewals:
                items = items.map(x => {
                    x["originRenewalString"] = x.GetRenewalString();
                    return x;
                });
                items = ArrayHelper.SortDynamic(items, "originRenewalString", sortDirection == SortDirection.DESC);
                break;

            case MedicalSummaryMedicationDetailsSortColumn.Status:
                items = items.map(x => {
                    x["statusSortString"] = x.GetStatusString();
                    return x;
                });
                items = ArrayHelper.SortDynamic(items, "statusSortString", sortDirection == SortDirection.DESC);
                break;

            case MedicalSummaryMedicationDetailsSortColumn.LongTerm:
                const groupedItems = ArrayHelper
                    .GroupBy(items, p => p.isLongTerm())
                    .sortByProperty(items => items[0]?.isLongTerm(), sortDirection === SortDirection.DESC);

                const sortedGroupedItems = [];
                for (const group of groupedItems) {
                    for (const item of items) {
                        const date = item.Model.PrescribeDate ?? item.Model.RefillStartDate;
                        item["longTermSortString"] = date ? moment(date).format("YYYY-MM-DD-h-mm-ss") : "";
                    }

                    const sortedGroup = ArrayHelper.SortDynamic(group, "longTermSortString", sortDirection === SortDirection.DESC);
                    sortedGroupedItems.push(...sortedGroup);
                }

                items = sortedGroupedItems;
                break;
        }

        if (items && items.length > 0) {
            return items.map(x => {
                let tmpMedTableItem = new MedicalItemMedicationAndPrescriptionTableItem(x);
                tmpMedTableItem.ShowInstruction = showInstructions;
                return tmpMedTableItem;
            });
        }
    }

    public filterAndSortMedication(patientId: string, items: MedicationModel[]): MedicationModel[] {
        let medicationGridModel = this.PatientMedicationGridModels[patientId];

        // start by filtering
        items = items.filter(x => {
            let isValid = true;
            let dateFrom = medicationGridModel.get("FromDate");
            let dateTo = medicationGridModel.get("ToDate");
            let statuses = medicationGridModel.get("StatusChecks").toArray();
            let origins = medicationGridModel.get("OriginChecks").toArray();

            // filter
            if (isValid && (dateFrom || dateTo)) {
                isValid = MedicationHelper.isMedicationDateBetweenDateFromAndDateTo(x.Model.RefillStartDate, x.EffectiveEndDate(), dateFrom, dateTo);
            }

            if (isValid && !(statuses == null) && statuses.length > 0) {
                let checkedStatus = statuses.filter(x => x.Checked);

                const checkedStatuses: MedicationsStatus[] = [];

                for (const check of checkedStatus) {
                    switch (check.Target) {
                        case CheckboxTarget.Myle_PatientCPP_MedicationSearch_Status_ActiveCheck:
                            checkedStatuses.push(MedicationsStatus.Active);
                            break;

                        case CheckboxTarget.Myle_PatientCPP_MedicationSearch_Status_ExpiredCheck:
                            checkedStatuses.push(MedicationsStatus.Expired);
                            break;

                        case CheckboxTarget.Myle_PatientCPP_MedicationSearch_Status_CompletedCheck:
                            checkedStatuses.push(MedicationsStatus.Completed);
                            break;

                        case CheckboxTarget.Myle_PatientCPP_MedicationSearch_Status_CeasedCheck:
                            checkedStatuses.push(MedicationsStatus.Ceased);
                            break;
                    }
                }

                // This logic is synced with the backend GenerateActiveMedicationPdfRequestHandler.SearchStatus method;
                // make sure to update both places when making changes.
                isValid =
                    (cacheStore.ClinicSettings.EnableLongTermMedication && x.Model.IsExpiredLongTermMed)
                    || checkedStatuses.contains(x.Model.VisibleForDisplayStatuses);
            }

            if (isValid && origins != null && origins.length > 0) {
                let checkedOrigins = origins.filter(x => x.Checked);

                let origin = x.GetOriginString();
                if (origin == "MYLE")
                    isValid = checkedOrigins.indexOfObject("Target", CheckboxTarget.Myle_PatientCPP_MedicationSearch_Origin_MyleCheck) >= 0;
                else if (origin.indexOf("DSQ") >= 0)
                    isValid = checkedOrigins.indexOfObject("Target", CheckboxTarget.Myle_PatientCPP_MedicationSearch_Origin_DSQCheck) >= 0;
                else if (origin.indexOf("Rx") >= 0 && origin.indexOf("DSQ") == -1)
                    isValid = checkedOrigins.indexOfObject("Target", CheckboxTarget.Myle_PatientCPP_MedicationSearch_Origin_RxCheck) >= 0;
            }
            return isValid;
        });

        items = items.sort((curr, next) => {
            let result;
            let isDescending = false;

            if (curr.Model.EMRTemplateSortIndex == next.Model.EMRTemplateSortIndex)
                result = 0;
            else
                result = (curr.Model.EMRTemplateSortIndex > next.Model.EMRTemplateSortIndex) ? 1 : -1;

            return isDescending ? (-1 * result) : result;
        });

        return items;
    }

    /* Will replace a patient's portal information with what is supplied in parameter
    * @param patientContactId
    * @param newPortalId
    * @param newPortalState
    * @param newOverView
    */
    public updatePatientPortalInfo(patientContactId: string, newPortalId: string, newPortalState: PortalRegistrationState, newOverView: GetPatientOverviewResponse): void {
        let patient: Patient = this.PatientModels[patientContactId];
        if (patient != null) {
            // change info in individual
            if (patient.IndividualResponse != null) {
                let individualModel = patient.IndividualResponse;
                individualModel.PortalProfileId = newPortalId;
                individualModel.PortalSyncState = newPortalState;
            }

            // change info in patient banner
            let bannerModel = this.PatientBannerModels[patientContactId];
            bannerModel = bannerModel.set("Model", patient);
            bannerModel = bannerModel.set("ModelUpdate", GuidHelper.NewGuid());
            this.PatientBannerModels[patientContactId] = bannerModel;

            // change info in search results
            let searchedPatients = sidebarStore.Model.get("SearchResultsPatients").toArray();
            let foundPatient: Person = ArrayHelper.Find(
                searchedPatients,
                (x: Person) => x.ContactFacetId == patientContactId
            );
            if (foundPatient != null) {
                foundPatient.PortalSyncState = newPortalState ? newPortalState : 0;
                foundPatient.PortalProfileId = newPortalId;
            }

            // change portal overview
            this.PatientPortalOverviewModels[patientContactId] = this.PatientPortalOverviewModels[patientContactId].set("PatientProfileOverview", newOverView);
            this.PatientModels[patientContactId] = patient;
        }
    }

    public updatePatientPortalState(patientContactId: string, newPortalState: PortalRegistrationState): void {
        const patient: Patient = this.PatientModels[patientContactId];
        if (patient != null) {
            // change info in individual
            if (patient.IndividualResponse != null) {
                const individualModel = patient.IndividualResponse;
                individualModel.PortalSyncState = newPortalState;
            }

            // change info in patient banner
            let bannerModel = this.PatientBannerModels[patientContactId];
            bannerModel = bannerModel.set("Model", patient);
            bannerModel = bannerModel.set("ModelUpdate", GuidHelper.NewGuid());
            this.PatientBannerModels[patientContactId] = bannerModel;

            // change info in search results
            const searchedPatients = sidebarStore.Model.get("SearchResultsPatients").toArray();
            const foundPatient: Person = ArrayHelper.Find(
                searchedPatients,
                (x: Person) => x.ContactFacetId == patientContactId
            );
            if (foundPatient != null) {
                foundPatient.PortalSyncState = newPortalState ? newPortalState : 0;
            }
        }
    }

    public getPortalPatientOverview(patientContactId: string, receiveOverviewCallback: (overview: GetPatientOverviewResponse) => void): void {
        const overviewExistsInPatientStore =
            !!this.PatientPortalOverviewModels[patientContactId]
            && !!this.PatientPortalOverviewModels[patientContactId].get("PatientProfileOverview");

        if (overviewExistsInPatientStore) {
            receiveOverviewCallback(this.PatientPortalOverviewModels[patientContactId].get("PatientProfileOverview"));
            return;
        }

        Client.Post(
            ServerOperations.Server.Portal_Proxy_GetPatientPortalProfileOverview,
            { PatientContactId: patientContactId },
            response => {
                let overview = response.Overview as GetPatientOverviewResponse;
                receiveOverviewCallback(overview);
            },
            () => {
                receiveOverviewCallback(null);
            }
        );
    }

    public handlePrivateBillProviderSelectionChange(privateBillModel: IPatientPrivateBillModel, selectedProvider: Person): IPatientPrivateBillModel {
        let selectedClinicians = privateBillModel.get("SelectedClinicians") != null
            ? privateBillModel.get("SelectedClinicians").toArray()
            : [];

        if (selectedClinicians.indexOf(selectedProvider) > -1)
            selectedClinicians = selectedClinicians.filter(x => x.ContactId != selectedProvider.ContactId);
        else
            selectedClinicians = [selectedProvider];

        if (selectedClinicians.length > 0) {
            let employee = cacheStore.GetEmployeeByContactIdOrEmpty(selectedClinicians[0].ContactId);
            let employeeTitle = cacheStore.GetLookupById(employee.PrefixLookupId);

            privateBillModel = privateBillModel.set("SelectedClinicianName", employeeTitle.GetValue() + " " + employee.LastName + ", " + employee.FirstName);
        }

        if (sessionStore.rememberPrivateBillProvider === true && selectedClinicians.length > 0)
            sessionStore.privateBillProviderContactFacetId = selectedClinicians[0].ContactFacetId;

        privateBillModel = privateBillModel.set("SelectedClinicians", Immutable.List<Person>(selectedClinicians));

        return privateBillModel;
    }

    public onRequestDeletedSuccess(requestId: string, encounterId: Guid): void {
        let patientContactId = this.ActivePatientContactId;
        let patientNoteModel = this.PatientMedicalNoteModels[patientContactId];
        let patientMedicalSummaryModel = this.PatientMedicalSummaryModels[patientContactId];

        if (patientNoteModel) {
            let requests = patientNoteModel.get("Requests");
            let newrequestList = requests?.filter(x => x.RequestId != requestId)?.toList();

            patientNoteModel = patientNoteModel.set("Requests", newrequestList);
            this.PatientMedicalNoteModels[patientContactId] = patientNoteModel;

            if (!cacheStore.ClinicSettings.EnableFollowUps)
                this.EmitChangeToListeners();
        }

        if (patientMedicalSummaryModel) {
            const requests = patientMedicalSummaryModel.get("Requests");
            const newRequestList = requests?.filter(x => x.RequestId != requestId)?.toList();

            patientMedicalSummaryModel = patientMedicalSummaryModel.set("Requests", newRequestList);
            this.PatientMedicalSummaryModels[patientContactId] = patientMedicalSummaryModel;
        }

        this.updateContributorsEncountersData(patientContactId, encounterId);
        this.ActionsMedicalSummary.reloadFollowUpScreeningData();
    }

    public onConsultationRequestDeletedSuccess(consultationRequestId: string, encounterId: Guid): void {
        let patientContactId = this.ActivePatientContactId;
        let patientNoteModel = this.PatientMedicalNoteModels[patientContactId];
        let patientMedicalSummaryModel = this.PatientMedicalSummaryModels[patientContactId];

        if (patientNoteModel) {
            let consultationRequests = patientNoteModel?.get("ConsultationRequests");
            let newConsultationRequestList = consultationRequests?.filter(x => x.ConsultationRequestId != consultationRequestId)?.toList();

            patientNoteModel = patientNoteModel.set("ConsultationRequests", newConsultationRequestList);
            this.PatientMedicalNoteModels[patientContactId] = patientNoteModel;

            if (!cacheStore.ClinicSettings.EnableFollowUps)
                this.EmitChangeToListeners();
        }

        if (patientMedicalSummaryModel) {
            const consultationRequests = patientMedicalSummaryModel?.get("ConsultationRequests");
            const newConsultationRequestList = consultationRequests?.filter(x => x.ConsultationRequestId != consultationRequestId)?.toList();

            patientMedicalSummaryModel = patientMedicalSummaryModel.set("ConsultationRequests", newConsultationRequestList);
            this.PatientMedicalSummaryModels[patientContactId] = patientMedicalSummaryModel;
        }

        this.updateContributorsEncountersData(patientContactId, encounterId);
        this.ActionsMedicalSummary.reloadFollowUpScreeningData();
    }

    public updateScreeningsModelAfterPrintingRequest(consultationRequestId: string, medicalRequestType: MedicalRequestType): void {
        let currentPatientContactId = this.getCurrentPatientContactId();
        let updateFollowUpRequest: (items: Immutable.List<MedicalItem>) => Immutable.List<any> =
            items => {
                if (!items)
                    return [].toImmutable();

                let filteredItems = items.toArray();
                for (let x of filteredItems) {
                    if (x.EMRTemplateMedicalItemType !== MedicalItemTypeEnum.ScreeningTest)
                        continue;

                    let screeningtest = x as MedicalItemScreeningTestResponse;
                    if (screeningtest.FollowUps) {
                        let followup = screeningtest.FollowUps.firstOrDefault(f => f.Id === consultationRequestId && f.MedicalRequestType === medicalRequestType);
                        if (followup) {
                            followup.IsPrinted = true;
                        }
                    }
                }
                return filteredItems.toImmutable();
            };

        let itemModel = this.PatientMedicalItemsModels[currentPatientContactId];
        let notesModel = this.PatientMedicalNoteModels[currentPatientContactId];
        let summaryModel = this.PatientMedicalSummaryModels[currentPatientContactId];

        if (itemModel)
            itemModel = itemModel.set("AllMedicalItemsList", updateFollowUpRequest(itemModel.get("AllMedicalItemsList")));

        if (notesModel)
            notesModel = notesModel.set("MedicalItemsScreeningTest", updateFollowUpRequest(notesModel.get("MedicalItemsScreeningTest")));

        if (summaryModel)
            summaryModel = summaryModel.set("MedicalItemsScreeningTest", updateFollowUpRequest(summaryModel.get("MedicalItemsScreeningTest")));
    }

    public getActivePatientHealthCards(): PatientIdentifier[] {
        return this.PatientProfileIdentifiersModels[this.ActivePatientContactId]?.get("HealthCards").toArray();
    }

    public updateContacts(response: any): void {
        let patientContactId = this.getCurrentPatientContactId();
        let patientModel = this.PatientModels[patientContactId];
        let uiModel = this.PatientUIModels[patientContactId];
        const deletedContactPointsNotifiedInFutureAppointments: ContactPointNotifiedInFutureAppointment[] =
            response.DeletedContactPointsNotifiedInFutureAppointments;

        patientModel.Phones = response.PhoneNumbers ?? patientModel.Phones;
        patientModel.Addresses = response.Addresses ?? patientModel.Addresses;
        patientModel.Emails = response.Emails ?? patientModel.Emails;
        patientModel.ContactPoints = response.ContactPoints ?? patientModel.ContactPoints;
        patientModel.IndividualResponse.DoNotEmail = response.DoNotEmail ?? patientModel.IndividualResponse.DoNotEmail;

        const newContactsModel = patientModel.ToPatientContactsModel();

        this.PatientProfileContactsModels[patientContactId] = Immutable.fromJS(newContactsModel);
        this.PatientProfileContactsUndoModels[patientContactId] = Immutable.fromJS(newContactsModel);

        let bannerModel = this.PatientBannerModels[patientContactId];
        bannerModel = bannerModel.delete("Model");
        bannerModel = bannerModel.set("Model", patientModel);
        this.PatientBannerModels[patientContactId] = bannerModel;

        uiModel = uiModel.set("PatientProfileEditMode", EditMode.View);

        this.PatientModels[patientContactId] = patientModel;
        this.PatientUIModels[patientContactId] = uiModel;

        if (deletedContactPointsNotifiedInFutureAppointments?.any()) {
            let vocab = cacheStore.GetString(
                "AppointmentContactPointNotificationWarningLabel",
                {
                    Value: deletedContactPointsNotifiedInFutureAppointments
                        .sortByProperty(x => x.AppointmentStartTime)
                        .map(x => {
                            let subject = x.AppointmentSubject || "";
                            if (!IsNullOrWhiteSpace(subject))
                                subject += " - ";

                            return `${subject} ${moment(x.AppointmentStartTime).format("YYYY-MM-DD HH:mm:ss")}`;
                        })
                        .join("\\n")
                }
            );

            const splitLines = vocab.split("\\n");

            sessionStore.showPopup(new PopupModel({
                content: splitLines,
                title: cacheStore.GetString("WarningLabel")
            }));
        }
    }

    public updateContributorsEncountersData(patientContactId: Guid, encounterId: Guid): void {
        const contributorsEncountersData = this.getContributorsToEncounter(
            encounterId,
            sessionStore.getCurrentEmployee().ContactFacetId,
            this.PatientMedicalNoteModels[patientContactId],
            this.PatientMedicalSummaryModels[patientContactId]
        );
        if (contributorsEncountersData.noteModel)
            this.PatientMedicalNoteModels[patientContactId] = contributorsEncountersData.noteModel;
        if (contributorsEncountersData.summaryModel)
            this.PatientMedicalSummaryModels[patientContactId] = contributorsEncountersData.summaryModel;
        this.EmitChangeToListeners();
    }

    public getContributorsToEncounter(encounterId: Guid, contributorEmployeeId: Guid, noteModel: IPatientMedicalNoteModel, summaryModel: IPatientMedicalSummaryModel): IPatientContributorsEncounterData {
        if (noteModel) {
            let encounter = noteModel.get("Encounter");
            if (encounter && encounter.Id == encounterId) {
                if (!encounter.EncounterRelatedEmployees)
                    encounter.EncounterRelatedEmployees = [];

                if (!encounter.EncounterRelatedEmployees.some(x => x.EmployeeId == contributorEmployeeId)) {
                    let tmpRelEmp = new EncounterRelatedEmployee();
                    tmpRelEmp.EncounterId = encounterId;
                    tmpRelEmp.EmployeeId = contributorEmployeeId;
                    tmpRelEmp.FirstContribution = DateTime.local().toString();
                    encounter.EncounterRelatedEmployees.push(tmpRelEmp);

                    // Do not set the model often, once to try to add the missing employee at the beginning is ok.
                    // Otherwise you'll lose the last 3 seconds the user typed stuff in the note
                    noteModel = noteModel.set("Encounter", encounter);
                }
            }
        }

        if (summaryModel) {
            let encounters = summaryModel.get("Encounters").toArray();
            let encounter = encounters.firstOrDefault(x => x.Id == encounterId);
            if (encounter) {
                if (!encounter.EncounterRelatedEmployees)
                    encounter.EncounterRelatedEmployees = [];

                if (!encounter.EncounterRelatedEmployees.some(x => x.EmployeeId == contributorEmployeeId)) {
                    let tmpRelEmp = new EncounterRelatedEmployee();
                    tmpRelEmp.EncounterId = encounterId;
                    tmpRelEmp.EmployeeId = contributorEmployeeId;
                    tmpRelEmp.FirstContribution = DateTime.local().toString();
                    encounter.EncounterRelatedEmployees.push(tmpRelEmp);

                    summaryModel = summaryModel.delete("Encounters");
                    summaryModel = summaryModel.set("Encounters", Immutable.List(encounters));
                }
            }
        }
        return { summaryModel, noteModel };
    }

    public cleanEncountersCache(): void {
        const patientContactId = this.getCurrentPatientContactId();
        let currentPatientCache = this.PatientMedicalNotesCache[patientContactId];
        const encountersList = this.PatientMedicalSummaryModels[patientContactId]?.get("Encounters")?.toArray();
        if (!currentPatientCache?.any())
            return;

        if (!encountersList?.any()) {
            this.PatientMedicalNotesCache[patientContactId] = [];
            integrationBus.emit(new CleanEncountersCacheEvent({ patientContactId, remainingEncounterIds: new Set<Guid>() }));
            return;
        }

        const encounterIds = new Set(encountersList.map(p => p.Id));

        currentPatientCache = currentPatientCache.filter(p => encounterIds.has(p.get("Encounter").Id));
        this.PatientMedicalNotesCache[patientContactId] = currentPatientCache;
        integrationBus.emit(new CleanEncountersCacheEvent({ patientContactId, remainingEncounterIds: encounterIds }));
    }

    public toggleIgnorePublicationRules(): void {
        this.ignorePublishingRules = !this.ignorePublishingRules;
    }

    private tryLockPatientFile(patientContactId: Guid): boolean {
        console.debug(`[ActivePatientFile] Try locking patient ${patientContactId}`);
        return actionLock.lock(`setActivePatientUI_${patientContactId}`, 5000);
    }

    public UnlockPatientFile(patientContactId: Guid): void {
        console.debug(`[ActivePatientFile] Unlocking patient ${patientContactId}`);
        actionLock.unlock(`setActivePatientUI_${patientContactId}`);
    }

    public addPrescriptionPrintHistory(prescriptionPrintHistory: PrescriptionPrintHistory, patientContactId: Guid): void {
        const patientMedicalSummary = patientStore.PatientMedicalSummaryModels[patientContactId];
        const prescriptions = patientMedicalSummary.get("Prescriptions");
        const prescription = prescriptions.find(p => p.PrescriptionId == prescriptionPrintHistory.PrescriptionId);

        prescription.PrintHistory = [...prescription.PrintHistory, prescriptionPrintHistory];

        patientMedicalSummary.set("Prescriptions", prescriptions);
    }
}

export { PatientStore, defaultInstance as patientStore };
